import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { AIONZ_TOOLTIP_DATA, AionzDialog, DialogSize } from '@aionzdev/design-system';
import { of } from 'rxjs';
import { PurchaseHistory } from '../../../interfaces/purchase-history';
import { IpcaService } from '../../../services/ipca.service';
import { environment } from '../../../../../../environments/environment';
import { PurchaseHistoryDialogData } from '../../../interfaces/product-purchase-history';
import { PurchaseHistoryDialogComponent } from '../../components/purchase-history-dialog/purchase-history-dialog.component';

interface DisplayRow {
  /** Ligadura do Material Symbols que representa a informação. */
  icon: string;
  label: string;
  value: string;
  /** Complemento discreto ao lado do valor (ex.: a data absoluta). */
  hint?: string;
  valueClass?: 'default' | 'success' | 'warning';
  /** Texto completo para o title quando o valor é resumido (ex.: lista com "+N"). */
  fullValue?: string;
}

@Component({
  selector: 'aionz-purchase-history-tooltip',
  imports: [MatDividerModule, MatIconModule],
  templateUrl: './purchase-history-tooltip.component.html',
  styleUrl: './purchase-history-tooltip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchaseHistoryTooltipComponent {
  protected readonly data = inject(AIONZ_TOOLTIP_DATA) as PurchaseHistory;
  private readonly dialog = inject(AionzDialog);

  /**
   * Com a rota do histórico no backend (chave por ambiente) ou no localhost, onde o modal mostra
   * dados de exemplo; e com o produto identificado.
   */
  protected readonly podeVerCompleto =
    (!!environment.historicoComprasCompleto || isLocalhost()) &&
    !!this.data?.productId &&
    !!this.data?.purchaseId;

  protected verHistoricoCompleto(): void {
    if (!this.podeVerCompleto) return;
    this.dialog.open<PurchaseHistoryDialogComponent, PurchaseHistoryDialogData>(
      PurchaseHistoryDialogComponent,
      {
        size: DialogSize.LG,
        data: {
          purchaseId: this.data.purchaseId!,
          productId: this.data.productId!,
          productName: this.data.productName ?? 'Produto',
          unitLabel: this.data.unitLabel,
          lastPurchasePrice: this.data.lastPurchasePrice,
          lastPurchaseDate: this.data.lastPurchaseDate,
          supplierNames: [
            ...new Set(
              [this.data.lastPurchaseSupplierName, ...(this.data.previousSuppliers ?? []).map(s => s.name)].filter(
                (name): name is string => !!name
              )
            )
          ],
          currentLowestPrice: this.data.currentLowestPrice
        }
      }
    );
  }

  private readonly currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });

  private readonly quantityFormatter = new Intl.NumberFormat('pt-BR');
  private readonly percentFormatter = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
  private readonly monthFormatter = new Intl.DateTimeFormat('pt-BR', { month: 'short', year: '2-digit' });

  /** IPCA acumulado desde o mês da última compra (Banco Central); null enquanto carrega. */
  private readonly ipca = toSignal(this.ipcaDesdeUltimaCompra(), { initialValue: null });

  /**
   * Último preço pago corrigido pelo IPCA: o "preço balizador" para negociar (pedido da
   * Conceito). Com cotação aberta, diz também quanto o menor preço cotado agora fica dele.
   */
  protected readonly ipcaRow = computed(() => {
    const ipca = this.ipca();
    const last = this.data?.lastPurchasePrice;
    if (!ipca || last == null || last <= 0) return null;

    if (ipca.pendente) {
      return {
        label: 'Corrigido pelo IPCA',
        value: this.formatCurrency(last),
        hint: `IPCA de ${this.formatMonth(ipca.inicio)} ainda não divulgado`,
        comparison: null
      };
    }

    const corrected = last * ipca.fator;
    const variation = (ipca.fator - 1) * 100;
    const sign = variation > 0 ? '+' : variation < 0 ? '−' : '';
    const start = this.formatMonth(ipca.inicio);
    const end = this.formatMonth(ipca.fim);
    const period = start === end ? `IPCA de ${start}` : `IPCA ${start} a ${end}`;

    let comparison: { text: string; valueClass: DisplayRow['valueClass'] } | null = null;
    const current = this.data?.currentLowestPrice;
    if (current != null && current > 0) {
      const delta = ((current - corrected) / corrected) * 100;
      const pct = this.percentFormatter.format(Math.abs(delta));
      if (delta > 0.5) comparison = { text: `cotado agora ${pct}% acima`, valueClass: 'warning' };
      else if (delta < -0.5) comparison = { text: `cotado agora ${pct}% abaixo`, valueClass: 'success' };
      else comparison = { text: 'cotado agora no mesmo valor', valueClass: 'default' };
    }

    return {
      label: 'Corrigido pelo IPCA',
      value: this.formatCurrency(corrected),
      hint: `${sign}${this.percentFormatter.format(Math.abs(variation))}% · ${period}`,
      comparison
    };
  });
  protected get hasHistory(): boolean {
    if (!this.data) return false;
    return (
      this.data.lastPurchasePrice != null ||
      this.data.bestPrice != null ||
      this.data.averageQuantity != null ||
      this.data.lastPurchaseDate != null ||
      (this.data.previousSuppliers?.length ?? 0) > 0
    );
  }

  protected get title(): string {
    return this.data?.unitLabel
      ? `Histórico de compra (${this.data.unitLabel})`
      : 'Histórico de compra';
  }

  protected get summaryRows(): DisplayRow[] {
    return [
      {
        icon: 'payments',
        label: 'Último preço pago',
        value: this.formatCurrency(this.data.lastPurchasePrice)
      },
      {
        icon: 'savings',
        label: 'Melhor preço',
        value: this.formatCurrency(this.data.bestPrice),
        valueClass: 'success'
      },
      {
        icon: 'inventory_2',
        label: 'Quantidade média',
        value: this.formatQuantity(this.data.averageQuantity)
      }
    ];
  }

  /** Menor preço cotado agora versus o melhor preço histórico, em percentual. */
  protected get comparisonRow(): DisplayRow | null {
    const current = this.data?.currentLowestPrice;
    const best = this.data?.bestPrice;
    if (current == null || best == null || best <= 0) return null;
    const delta = ((current - best) / best) * 100;
    const pct = Math.abs(delta).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
    let hint: string;
    let valueClass: DisplayRow['valueClass'] = 'default';
    if (delta > 0.5) {
      hint = `${pct}% acima do seu melhor preço`;
      valueClass = 'warning';
    } else if (delta < -0.5) {
      hint = `${pct}% abaixo do seu melhor preço`;
      valueClass = 'success';
    } else {
      hint = 'igual ao seu melhor preço';
    }
    return { icon: 'compare_arrows', label: 'Menor preço cotado agora', value: this.formatCurrency(current), hint, valueClass };
  }

  protected get contextRows(): DisplayRow[] {
    return [
      {
        icon: 'schedule',
        label: 'Comprado',
        value: this.formatRelativeDays(this.data.lastPurchaseDate),
        hint: this.formatAbsoluteDate(this.data.lastPurchaseDate)
      },
      {
        icon: 'storefront',
        label: 'Último fornecedor',
        value: this.footerSupplierName
      },
      ...this.previousSuppliersRow
    ];
  }

  /** Quem já vendeu o item: ajuda a decidir quem convidar. Até 3 nomes, o resto como "+N". */
  private get previousSuppliersRow(): DisplayRow[] {
    const names = (this.data.previousSuppliers ?? []).map(s => s.name).filter(Boolean);
    if (!names.length) return [];
    const shown = names.slice(0, 3).join(', ');
    const rest = names.length - 3;
    return [
      {
        icon: 'groups',
        label: 'Já forneceram',
        value: rest > 0 ? `${shown} +${rest}` : shown,
        fullValue: names.join(', ')
      }
    ];
  }

  protected get footerSupplierName(): string {
    return this.data.lastPurchaseSupplierName ?? '--';
  }

  private ipcaDesdeUltimaCompra() {
    const raw = this.data?.lastPurchaseDate;
    if (!raw || this.data?.lastPurchasePrice == null) return of(null);
    const date = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(date.getTime())) return of(null);
    return inject(IpcaService).acumuladoDesde(date);
  }

  /** "nov/26" */
  private formatMonth(date: Date): string {
    return this.monthFormatter.format(date).replace('.', '').replace(' de ', '/');
  }

  private formatCurrency(value: number | null | undefined): string {
    if (value == null) {
      return '--';
    }

    return this.currencyFormatter.format(value);
  }

  private formatQuantity(value: number | null | undefined): string {
    if (value == null) {
      return '--';
    }

    return this.quantityFormatter.format(value);
  }

  private readonly dateFormatter = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });

  private formatAbsoluteDate(value: string | Date | null | undefined): string | undefined {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : this.dateFormatter.format(date);
  }

  private formatRelativeDays(value: string | Date | null | undefined): string {
    if (!value) {
      return '--';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '--';
    }

    const diffInDays = Math.max(
      0,
      Math.floor((Date.now() - date.getTime()) / 86_400_000)
    );

    if (diffInDays === 0) {
      return 'hoje';
    }

    return `há ${diffInDays} ${diffInDays === 1 ? 'dia' : 'dias'}`;
  }
}

function isLocalhost(): boolean {
  const host = globalThis.location?.hostname ?? '';
  return host === 'localhost' || host === '127.0.0.1';
}
